/*
 * <BeText> -- a BeOS text run.
 *
 * The React face of `.be-pixel-text`. Every string the library draws goes
 * through one of these, because a coverage-font run needs a per-engine
 * treatment (Notes/51) that only applies to a run element: the Gecko-Windows
 * ClearType blend, the WebKit run-origin snap, and the inline-block that makes
 * promotion possible at all.
 *
 * WHY A COMPONENT AND NOT JUST A CLASS. The class is still the contract -- this
 * library is CSS-first and a plain-HTML consumer writes `class="be-pixel-text"`
 * exactly as before. What the component adds is the two rules that are easy to
 * get wrong by hand and have both already shipped as bugs:
 *
 *   1. The treatment must not land on an element that paints a BACKGROUND.
 *      mix-blend-mode: multiply is identity only for ink on a transparent run;
 *      given a background it multiplies that against whatever is behind. This
 *      bit `.be-box__title` (masks the fieldset border) and `.be-checkbox__label`
 *      (paints the focus underline, measured darkening nav blue from
 *      rgb(0,0,229) to rgb(0,0,128)). Pass `surface` and the class moves to an
 *      inner run, leaving the painted box alone.
 *
 *   2. The treatment must not land on a non-text child. `label` is a ReactNode
 *      and callers pass whole controls -- BBox's demo passes a <BeMenuField>,
 *      whose well went dark for the same reason. Non-text children are passed
 *      through untreated; their own components treat their own runs.
 *
 * `face` maps to the R5 system fonts (Notes/40 §system fonts): control =
 * be_plain_font 10, menu = plain 12, tab = be_bold_font BOLD 12 (menu and tab
 * differ only in weight), mini = plain 9, Tracker's column header. It sets those
 * from the tokens so a caller does not restate them; omit it to inherit, which
 * is what component-internal runs want.
 *
 * Family and size are ONE decision, never two: each coverage face is native at
 * exactly one ppem, so a size without its family resamples.
 */

import type { ElementType, HTMLAttributes, ReactNode } from "react";
import { type BeCommon, beComponent } from "./create.tsx";
import { isTextual, pixelText } from "./pixel-text.tsx";

/**
 * The run roles. `control` and `tab` are R5's system fonts; `h1`-`h4` are the
 * display ladder -- Dutch801 disk strikes at an integer magnification, which is
 * a face rather than a heading level, so `as` still carries the semantics.
 * Omit to inherit from the surrounding control.
 */
export type BeTextFace = "control" | "menu" | "tab" | "mini" | "mono" | "h1" | "h2" | "h3" | "h4";

/**
 * Semantic inks, named from BeStatusTone rather than a vocabulary of their own.
 *
 * `warning` and `neutral` are deliberately absent: measured against the 216
 * panel they are 1.35:1 and 1.67:1, under even the 3:1 non-text floor. They are
 * FILL tones, where an adjacent label carries the meaning. See text-run.css.
 */
export type BeTextInk = "secondary" | "danger" | "success" | "info";

/* Polymorphic (`as="code"`, `as="time"`, `as="li"`), so the DOM attributes come
 * from the generic HTMLAttributes -- see create.tsx. */
export type BeTextProps = BeCommon &
	Omit<HTMLAttributes<HTMLElement>, "className" | "style"> & {
		children?: ReactNode;
		/**
		 * Size + family from the R5 system-font tokens. Omit to inherit -- correct
		 * for a run inside a control that already sets them.
		 */
		face?: BeTextFace;
		/**
		 * The element to render. Defaults to <span>. Use for semantics
		 * (`as="time"`, `as="legend"`), not for styling.
		 */
		as?: ElementType;
		/**
		 * Set when THIS element paints a background (a mask, a fill, a focus
		 * gradient). The treatment moves to an inner run so the blend cannot
		 * multiply the background against the page. See rule 1 above.
		 */
		surface?: boolean;
		/** Yield and ellipsize rather than push the row wider; a plain-string child
		 * becomes the `title`, so the clipped text stays readable. */
		truncate?: boolean;
		/** Semantic ink. Omit for the surface default. */
		ink?: BeTextInk;
		/** The bold strike. */
		bold?: boolean;
		/** The italic strike. */
		italic?: boolean;
		/** Struck through. */
		strike?: boolean;
	};

export const BeText = beComponent<BeTextProps, HTMLElement>("BeText", (props, be) => {
	const { children, face, ink, bold, italic, strike, as: Tag = "span", surface, truncate, scale, className, style, ...rest } = props;
	// shrink AND truncate: a row child keeps its own size by default (stack.css),
	// so clipping alone would never get the chance to apply.
	const mods = [
		truncate && "be-shrink",
		truncate && "be-truncate",
		ink && `be-ink-${ink}`,
		bold && "be-text--bold",
		italic && "be-text--italic",
		strike && "be-text--strike",
	];
	// Clipping text without saying what was clipped is the half-done version, so
	// the prop carries both. Before ...rest, so an explicit title still wins.
	const title = truncate && typeof children === "string" ? children : undefined;

	// This element paints something, so it must not carry the blend. Keep its own
	// classes here and put the treated run inside.
	if (surface) {
		const root = be(face && `be-text--${face}`, ...mods);
		return (
			<Tag title={title} {...rest} {...root} className={root.className || undefined}>
				{pixelText(children)}
			</Tag>
		);
	}

	// Otherwise this element IS the run -- but only if it holds text. A non-text
	// child (a whole control) must not be wrapped in the blend; it renders as
	// itself and its own component treats its own runs.
	const root = be(face && `be-text--${face}`, isTextual(children) && "be-pixel-text", ...mods);
	return (
		<Tag title={title} {...rest} {...root} className={root.className || undefined}>
			{children}
		</Tag>
	);
});
